import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Play, Star, Clock, ChevronRight, Mic } from "lucide-react";
import { HERO_IMAGES, HERO_SLIDES, formatDuration } from "../mock";
import { useLang } from "../context/LanguageContext";
import { useCatalog } from "../context/CatalogContext";
import { usePlayer } from "../context/PlayerContext";
import { Button } from "./ui/button";

const INTERVAL = 6500;

export default function HeroCarousel() {
  const { t } = useLang();
  const { books, getBook } = useCatalog();
  const navigate = useNavigate();
  const { playTrack } = usePlayer();
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const frameRef = useRef(null);

  /**
   * Slides come from whatever is flagged Featured in the CMS, falling back to
   * the curated list. The old version hardcoded three seed ids, so deleting or
   * unpublishing any of them made the whole 560px section vanish for six
   * seconds and reappear as the carousel came round — a jump on the homepage
   * with nothing in the CMS to explain it.
   */
  const slides = useMemo(() => {
    const featured = books.filter((b) => b.featured).slice(0, 5);
    if (featured.length) {
      return featured.map((b, i) => ({
        book: b,
        image: HERO_IMAGES[i % HERO_IMAGES.length],
        tagline_en: HERO_SLIDES[i % HERO_SLIDES.length].tagline_en,
      }));
    }
    return HERO_SLIDES
      .map((s) => ({ ...s, book: getBook(s.bookId) }))
      .filter((s) => s.book);
  }, [books, getBook]);

  // Respect a reduced-motion preference, and stop while the reader is hovering
  // or tabbing through: WCAG 2.2.2 requires anything auto-advancing past five
  // seconds to be pausable.
  const reduceMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (paused || reduceMotion || slides.length < 2) return;
    frameRef.current = setInterval(
      () => setIndex((i) => (i + 1) % slides.length),
      INTERVAL,
    );
    return () => clearInterval(frameRef.current);
  }, [paused, reduceMotion, slides.length]);

  useEffect(() => {
    if (index >= slides.length) setIndex(0);
  }, [slides.length, index]);

  if (!slides.length) return null;
  const slide = slides[Math.min(index, slides.length - 1)];
  const book = slide.book;
  const to = `/book/${book.slug || book.id}`;

  return (
    <section
      className="relative h-[560px] sm:h-[600px] overflow-hidden"
      aria-roledescription="carousel"
      aria-label="Featured audiobooks"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      {slides.map((s, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
          aria-hidden={i !== index}
        >
          <img
            src={s.image}
            alt=""
            // The hero image is the largest thing on the page and therefore the
            // LCP element: the first one is fetched eagerly and at high
            // priority, the rest stay lazy so they do not compete with it.
            loading={i === 0 ? "eager" : "lazy"}
            fetchpriority={i === 0 ? "high" : "low"}
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-gradient" />
        </div>
      ))}

      <div className="relative z-10 h-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
        <div className="max-w-xl" key={index}>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-brand-500/15 border border-brand-500/30 text-brand-300 text-xs font-semibold mb-5 animate-float-up">
            {slide.tagline_en}
          </span>
          {/* The featured title is the way into its page, which frees the
              button below to mean what it says and go to the catalogue. */}
          <h1 className="font-display text-4xl sm:text-6xl font-semibold leading-[1.05] tracking-tight animate-float-up">
            <Link to={to} className="hover:text-brand-300 transition-colors">{book.title_en}</Link>
          </h1>
          <p className="text-muted-foreground mt-3 text-lg animate-float-up">
            {t("written_by")}{" "}
            <Link
              to={`/author/${encodeURIComponent(book.author)}`}
              className="text-foreground font-medium hover:text-brand-400 transition-colors"
            >
              {book.author}
            </Link>
            {book.narrator && (
              <>
                {" · "}
                <span className="inline-flex items-center gap-1">
                  <Mic className="w-4 h-4" />
                  <Link
                    to={`/narrator/${encodeURIComponent(book.narrator)}`}
                    className="text-foreground font-medium hover:text-brand-400 transition-colors"
                  >
                    {book.narrator}
                  </Link>
                </span>
              </>
            )}
          </p>
          <div className="flex items-center gap-5 mt-4 text-sm animate-float-up">
            <span className="flex items-center gap-1 text-brand-400 font-semibold">
              <Star className="w-4 h-4" fill="currentColor" /> {Number(book.rating || 0).toFixed(1)}
            </span>
            <span className="flex items-center gap-1.5 text-muted-foreground">
              <Clock className="w-4 h-4" /> {formatDuration(book.duration)}
            </span>
          </div>
          <p className="text-muted-foreground/90 mt-5 leading-relaxed line-clamp-2 animate-float-up">
            {book.desc_en}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-7 animate-float-up">
            <Button
              onClick={() => playTrack(book, null, true)}
              className="bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-full h-12 px-7 text-base"
            >
              <Play className="w-5 h-5 mr-1" fill="currentColor" /> {t("hero_cta")}
            </Button>
            {/* Goes to the catalogue, which is what the label promises. It used
                to open the single featured book, so the button read as broken;
                that route now hangs off the title above. */}
            <Button
              variant="outline"
              onClick={() => navigate("/browse")}
              className="rounded-full h-12 px-7 text-base border-white/25 bg-white/5 hover:bg-white/10 backdrop-blur"
            >
              {t("hero_explore")} <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </div>

      {slides.length > 1 && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
          {slides.map((s, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`Show ${s.book.title_en}`}
              aria-current={i === index}
              className={`h-1.5 rounded-full transition-all focus-visible:ring-2 focus-visible:ring-brand-400 ${
                i === index ? "w-8 bg-brand-400" : "w-2.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
}
